import React from 'react';
import { type InputHTMLAttributes, useRef } from 'react';
import cn from 'classnames';
import { useField } from 'formik';
import { v4 as uuid } from 'uuid';

import type { FieldAttributes } from './Field';
import FieldValidationMessage from './ValidationMessage';

interface Props extends InputHTMLAttributes<HTMLInputElement> {
  children: React.ReactNode | string;
  primary?: boolean;
}

export default function Radio({ children, id, className, primary, ...props }: Props) {
  const idRef = useRef(id ?? uuid());

  return (
    <label className={cn('radio', className, { primary })} htmlFor={idRef.current}>
      <input type="radio" {...props} id={idRef.current} />
      <div className="radio-label">{children}</div>
    </label>
  );
}

export interface RadioFieldProps extends FieldAttributes<HTMLInputElement> {
  value: string | number | readonly string[];
  children: React.ReactNode | string;
  showValidation?: boolean;
}

export function RadioField({ name, value, className, showValidation, children, ...props }: RadioFieldProps) {
  const idRef = useRef(props.id ?? uuid());
  const [field, meta, helpers] = useField(name);

  return (
    <label className={cn('radio', { 'radio-error': meta.touched && !!meta.error }, className)} htmlFor={idRef.current}>
      <input
        type="radio"
        {...props}
        checked={value === field.value}
        id={idRef.current}
        name={name}
        onChange={() => helpers.setValue(value, true)}
        value={value}
      />
      <div className="radio-label">{children}</div>
      {showValidation ? <FieldValidationMessage name={name} /> : null}
    </label>
  );
}
